import type { ProductCard as Card } from '@shop/shared';
import { Link, useLocale, useT } from '../lib/i18n';
import { useMoney } from '../lib/settings';
import { Img, ItemFacts, Skeleton, Stamp } from './ui';

// Photos on a table are never perfectly square to each other.
const TILTS = ['-rotate-[0.6deg]', 'rotate-[0.45deg]', '-rotate-[0.25deg]', 'rotate-[0.7deg]'];

export function ProductCard({ product, priority, sizes = '(min-width: 1024px) 25vw, 50vw', index = 0 }: { product: Card; priority?: boolean; sizes?: string; index?: number }) {
  const t = useT();
  const locale = useLocale();
  const money = useMoney();
  const onSale = product.salePrice !== null && product.salePrice < product.price;
  const current = onSale ? product.salePrice! : product.price;
  const showFrom = product.hasVariants && product.priceMax > current;
  const alt = (locale === 'en' && product.image?.alt) || product.title;

  return (
    <article className="group relative">
      <Link to={`/products/${product.slug}`} className="block" aria-label={product.title}>
        <div className={`photo transition-transform duration-500 group-hover:rotate-0 ${TILTS[index % TILTS.length]} ${product.inStock ? '' : 'sold-photo'}`}>
          <div className="photo-inner grain aspect-[4/5]">
            <Img
              src={product.image?.url}
              thumb={product.image?.thumbUrl}
              alt={alt}
              sizes={sizes}
              priority={priority}
              className="absolute inset-0 h-full w-full object-cover group-hover:scale-[1.03]"
            />
            {product.hoverImage && product.inStock && (
              <Img
                src={product.hoverImage.url}
                thumb={product.hoverImage.thumbUrl}
                alt=""
                sizes={sizes}
                className="absolute inset-0 h-full w-full object-cover opacity-0! transition-opacity duration-700 group-hover:opacity-100!"
                aria-hidden
              />
            )}
            <div className="pointer-events-none absolute top-3 left-3 flex flex-col items-start gap-2">
              {product.inStock && product.oneOfAKind && (
                <Stamp tone="ink" className="-rotate-3 bg-paper/80 max-sm:px-1.5! max-sm:text-[9.5px]! max-sm:tracking-[0.08em]!">
                  {t.card.oneOfAKind}
                </Stamp>
              )}
              {product.inStock && onSale && (
                <Stamp className="rotate-2 bg-paper/80 max-sm:px-1.5! max-sm:text-[9.5px]! max-sm:tracking-[0.08em]!">{t.common.reduced}</Stamp>
              )}
            </div>
            {!product.inStock && (
              <div className="pointer-events-none absolute inset-0 flex items-center justify-center">
                <Stamp className="-rotate-12 bg-paper/70 px-4 py-2 text-[18px]!">{t.card.sold}</Stamp>
              </div>
            )}
            <span className="price-tag absolute right-2 bottom-3 rotate-[-4deg]">
              {showFrom && <span className="mr-1 font-normal text-stone">{t.common.from}</span>}
              <span className={onSale ? 'text-sale' : ''}>{money(current)}</span>
              {onSale && <s className="ml-1.5 font-normal text-stone">{money(product.price)}</s>}
            </span>
          </div>
        </div>
        <div className="mt-4 px-1">
          <h3 className="font-display text-[1.2rem] leading-snug font-medium group-hover:text-accent">{product.title}</h3>
          <div className="mt-1.5 flex items-center justify-between gap-3">
            <ItemFacts decade={product.decade} condition={product.condition} />
            {product.swatches.length > 0 && (
              <div className="flex shrink-0 items-center gap-1.5" aria-label={t.card.colours(product.swatches.length)}>
                {product.swatches.slice(0, 5).map((c) => (
                  <span key={c} className="h-3 w-3 rounded-full ring-1 ring-ink/25 ring-offset-1 ring-offset-bone" style={{ background: c }} />
                ))}
              </div>
            )}
          </div>
        </div>
      </Link>
    </article>
  );
}

export function ProductCardSkeleton() {
  return (
    <div>
      <div className="photo">
        <Skeleton className="aspect-[4/5] w-full" />
      </div>
      <div className="mt-4 space-y-2 px-1">
        <Skeleton className="h-5 w-2/3" />
        <Skeleton className="h-3.5 w-1/3" />
      </div>
    </div>
  );
}

export function ProductGrid({ products, loading, count = 8, columns = 4, priorityCount = 0 }: { products?: Card[]; loading?: boolean; count?: number; columns?: 3 | 4; priorityCount?: number }) {
  const cols = columns === 4 ? 'lg:grid-cols-4' : 'lg:grid-cols-3';
  return (
    <div className={`grid grid-cols-2 gap-x-4 gap-y-11 sm:gap-x-7 md:gap-y-14 ${cols}`}>
      {loading || !products
        ? Array.from({ length: count }, (_, i) => <ProductCardSkeleton key={i} />)
        : products.map((p, i) => (
            <ProductCard key={p.id} product={p} index={i} priority={i < priorityCount} sizes={columns === 4 ? '(min-width: 1024px) 25vw, 50vw' : '(min-width: 1024px) 28vw, 50vw'} />
          ))}
    </div>
  );
}
